/**
 * Yarnly Crochet — ugc-gallery.js
 *
 * <yarnly-ugc-carousel> for sections/ugc-gallery.liquid: a native CSS
 * scroll-snap row of maker videos, progressively enhanced.
 *
 *   <yarnly-ugc-carousel>
 *     <ul data-ugc-track tabindex="0" aria-label>  li[data-ugc-slide][data-block-id] …  </ul>
 *     <button data-ugc-prev hidden> <button data-ugc-next hidden>
 *   </yarnly-ugc-carousel>
 *
 * - Prev / next scroll one page (the cards that fully fit), carry
 *   aria-disabled="true" at the ends and stay hidden while nothing overflows.
 *   They are re-evaluated on scroll, on resize (ResizeObserver) and after
 *   every programmatic scroll.
 * - Drag to scroll with a mouse on fine pointers only (touch keeps the native
 *   swipe). The drag starts after 6 px, so clicks on the play buttons still
 *   work; the click that ends a real drag is swallowed, and the row settles on
 *   the nearest card.
 * - A click inside a card that is only partly visible first scrolls that card
 *   into view (instantly), so the video it starts is not paused at once by the
 *   video embed's "less than a quarter visible" rule.
 * - Theme Editor: shopify:block:select scrolls the selected card into view
 *   (matched by the event target or by detail.blockId).
 * - The track keeps tabindex="0" (keyboard scrolling) only while it scrolls.
 *
 * <yarnly-reviews-row> for sections/reviews.liquid (loaded from the same
 * file): wraps the testimonial list, a swipe row below 990px and a static grid
 * above. It keeps tabindex="0" on [data-scroll-row] only while that list
 * actually overflows, so the desktop grid is not a stray tab stop.
 *
 * The videos themselves are <yarnly-video-embed> (assets/theme.js). Plain
 * ES2020 IIFE, no dependencies beyond window.Yarnly; safe to evaluate twice.
 */
(function () {
  'use strict';

  const S = window.Yarnly;
  if (!S) return;

  const DRAG_THRESHOLD = 6;
  const EDGE = 2;

  function reducedMotion() {
    if (S.utils && typeof S.utils.isReducedMotion === 'function') return S.utils.isReducedMotion();
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function finePointer() {
    const query = '(hover: hover) and (pointer: fine)';
    if (S.utils && typeof S.utils.matchesMedia === 'function') return S.utils.matchesMedia(query);
    return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  }

  function nextFrame(fn) {
    if (typeof window.requestAnimationFrame === 'function') return window.requestAnimationFrame(fn);
    return window.setTimeout(fn, 16);
  }

  // A scroll container is a tab stop only while it can scroll.
  function syncTabStop(element, scrollable) {
    if (scrollable) element.setAttribute('tabindex', '0');
    else element.removeAttribute('tabindex');
  }

  if (!customElements.get('yarnly-ugc-carousel')) {
    class YarnlyUgcCarousel extends HTMLElement {
      constructor() {
        super();
        [
          'update',
          'schedule',
          'onArrow',
          'onPointerDown',
          'onPointerMove',
          'onPointerUp',
          'onClickCapture',
          'onBlockSelect'
        ].forEach((name) => {
          this[name] = this[name].bind(this);
        });
        this.ticking = false;
        this.drag = null;
        this.suppressClick = false;
      }

      connectedCallback() {
        this.track = this.querySelector('[data-ugc-track]');
        this.prev = this.querySelector('[data-ugc-prev]');
        this.next = this.querySelector('[data-ugc-next]');
        if (!this.track) return;

        this.track.addEventListener('scroll', this.schedule, { passive: true });
        this.track.addEventListener('pointerdown', this.onPointerDown);
        // Capture phase: runs before the video embed's own click handler.
        this.track.addEventListener('click', this.onClickCapture, true);
        [this.prev, this.next].forEach((button) => {
          if (button) button.addEventListener('click', this.onArrow);
        });
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.schedule);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.schedule, { passive: true });
        }
        this.update();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.endDrag();
        this.track.removeEventListener('scroll', this.schedule);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('click', this.onClickCapture, true);
        [this.prev, this.next].forEach((button) => {
          if (button) button.removeEventListener('click', this.onArrow);
        });
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        window.removeEventListener('resize', this.schedule);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      /* ------------------------------------------------------------ geometry */

      slides() {
        return Array.from(this.track.querySelectorAll('[data-ugc-slide]'));
      }

      maxScroll() {
        return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      // Scroll position that puts a slide at the start of the row.
      slideLeft(slide, slides) {
        const list = slides || this.slides();
        const origin = list.length ? list[0].offsetLeft : 0;
        return slide.offsetLeft - origin;
      }

      // Distance between two neighbouring cards (card width + gap).
      step() {
        const slides = this.slides();
        if (slides.length > 1) {
          const distance = Math.abs(slides[1].offsetLeft - slides[0].offsetLeft);
          if (distance > 0) return distance;
        }
        return this.track.clientWidth || 1;
      }

      // One page: every card that fully fits, at least one.
      pageStep() {
        const step = this.step();
        const cards = Math.max(1, Math.floor((this.track.clientWidth + EDGE) / step));
        return cards * step;
      }

      scrollToLeft(left, instant) {
        const target = Math.max(0, Math.min(left, this.maxScroll()));
        const behavior = instant || reducedMotion() ? 'auto' : 'smooth';
        if (typeof this.track.scrollTo === 'function') this.track.scrollTo({ left: target, behavior });
        else this.track.scrollLeft = target;
        this.schedule();
      }

      /* ------------------------------------------------------------- arrows */

      schedule() {
        if (this.ticking) return;
        this.ticking = true;
        nextFrame(() => {
          this.ticking = false;
          if (this.track) this.update();
        });
      }

      update() {
        const max = this.maxScroll();
        const left = this.track.scrollLeft;
        const overflow = max > EDGE;
        this.classList.toggle('is-scrollable', overflow);
        syncTabStop(this.track, overflow);
        [this.prev, this.next].forEach((button) => {
          if (button) button.hidden = !overflow;
        });
        this.setDisabled(this.prev, left <= EDGE);
        this.setDisabled(this.next, left >= max - EDGE);
      }

      setDisabled(button, disabled) {
        if (button) button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      onArrow(event) {
        const button = event.currentTarget;
        event.preventDefault();
        if (button.getAttribute('aria-disabled') === 'true') return;
        const direction = button === this.prev ? -1 : 1;
        const page = this.pageStep();
        const step = this.step();
        // Land on a card boundary even when the row was left mid-card.
        const base = Math.round(this.track.scrollLeft / step) * step;
        this.scrollToLeft(base + direction * page);
      }

      /* ------------------------------------------------------- Theme Editor */

      onBlockSelect(event) {
        const slides = this.slides();
        let slide = null;
        const target = event.target;
        if (target && typeof target.closest === 'function') {
          const hit = target.closest('[data-ugc-slide]');
          if (hit && this.track.contains(hit)) slide = hit;
        }
        const blockId = event.detail && event.detail.blockId;
        if (!slide && blockId) {
          slide = slides.find((item) => item.getAttribute('data-block-id') === String(blockId)) || null;
        }
        if (!slide) return;
        this.scrollToLeft(this.slideLeft(slide, slides), true);
      }

      /* -------------------------------------------------------------- drag */

      onPointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (!finePointer() || this.maxScroll() <= EDGE) return;
        this.endDrag();
        this.drag = {
          id: event.pointerId,
          startX: event.clientX,
          startLeft: this.track.scrollLeft,
          active: false
        };
        this.track.addEventListener('pointermove', this.onPointerMove);
        this.track.addEventListener('pointerup', this.onPointerUp);
        this.track.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        const delta = event.clientX - drag.startX;
        if (!drag.active) {
          if (Math.abs(delta) <= DRAG_THRESHOLD) return;
          drag.active = true;
          this.classList.add('is-dragging');
          if (typeof this.track.setPointerCapture === 'function') {
            try {
              this.track.setPointerCapture(drag.id);
            } catch (error) {
              // Pointer capture is optional.
            }
          }
        }
        event.preventDefault();
        this.track.scrollLeft = Math.max(0, Math.min(drag.startLeft - delta, this.maxScroll()));
      }

      onPointerUp(event) {
        const drag = this.drag;
        if (!drag || (event && event.pointerId !== drag.id)) return;
        const wasActive = drag.active;
        this.endDrag();
        if (!wasActive) return;
        // Swallow the click the browser fires right after this pointerup.
        this.suppressClick = true;
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 0);
        // Settle on the nearest card now that snapping is back on.
        const slides = this.slides();
        if (!slides.length) return;
        const left = this.track.scrollLeft;
        let nearest = 0;
        slides.forEach((slide) => {
          const offset = this.slideLeft(slide, slides);
          if (Math.abs(offset - left) < Math.abs(nearest - left)) nearest = offset;
        });
        this.scrollToLeft(nearest);
      }

      endDrag() {
        const drag = this.drag;
        this.drag = null;
        this.classList.remove('is-dragging');
        if (!this.track) return;
        if (drag && typeof this.track.releasePointerCapture === 'function') {
          try {
            this.track.releasePointerCapture(drag.id);
          } catch (error) {
            // Not captured.
          }
        }
        this.track.removeEventListener('pointermove', this.onPointerMove);
        this.track.removeEventListener('pointerup', this.onPointerUp);
        this.track.removeEventListener('pointercancel', this.onPointerUp);
      }

      /* ------------------------------------------------------------- clicks */

      onClickCapture(event) {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
          return;
        }
        const slide = event.target && event.target.closest ? event.target.closest('[data-ugc-slide]') : null;
        if (!slide || !this.track.contains(slide)) return;
        const box = slide.getBoundingClientRect();
        const view = this.track.getBoundingClientRect();
        const clipped = box.left < view.left - EDGE || box.right > view.right + EDGE;
        if (clipped && box.width > 0) this.scrollToLeft(this.slideLeft(slide), true);
      }
    }
    customElements.define('yarnly-ugc-carousel', YarnlyUgcCarousel);
  }

  if (!customElements.get('yarnly-reviews-row')) {
    class YarnlyReviewsRow extends HTMLElement {
      constructor() {
        super();
        this.schedule = this.schedule.bind(this);
        this.ticking = false;
      }

      connectedCallback() {
        this.list = this.querySelector('[data-scroll-row]');
        if (!this.list) return;
        window.addEventListener('resize', this.schedule, { passive: true });
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.schedule);
          this.resizeObserver.observe(this.list);
        }
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.schedule);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      schedule() {
        if (this.ticking) return;
        this.ticking = true;
        nextFrame(() => {
          this.ticking = false;
          if (this.list) this.update();
        });
      }

      update() {
        syncTabStop(this.list, this.list.scrollWidth - this.list.clientWidth > EDGE);
      }
    }
    customElements.define('yarnly-reviews-row', YarnlyReviewsRow);
  }
})();
